import React, { useEffect, useState, useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import APIService from "../../services/api.service";
import { CacheService } from "../../common/services/cache.service";
import environment from "../../environment/environment";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useRecoilValue, useSetRecoilState, useRecoilState } from "recoil";
import { tokenState } from "../../atoms/token";
import { cartState } from "../../atoms/cart";

export function ViewDownloadsDialog({
  title,
  closeButtonText,
  handleActionButtonText,
  handleDownloadItemsClick,
  handleCloseClick,
}: {
  title: string;
  closeButtonText: string;
  handleActionButtonText: string;
  handleDownloadItemsClick: (items: any[]) => void;
  handleCloseClick: () => void;
}) {
  const globalCart = useRecoilValue(cartState);
  const setGlobalCart = useSetRecoilState(cartState);
  const [token] = useRecoilState(tokenState);
  const [localCart, setLocalCart] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Initialize local cart once
  useEffect(() => {
    setLocalCart(globalCart);
    setLoading(false);
  }, [globalCart]);

  // Remove a single item from local cart
  const removeLocal = (ticParentDocId: string) => {
    setLocalCart(prev => prev.filter(item => item.parentDocId !== ticParentDocId));
  };

  // Remove all items locally
  const handleRemoveAllLocal = () => {
    setLocalCart([]);
  };

  // On Save, update global cart state
  const onSave = () => {
    const parentDocIds = localCart.map(item => item.parentDocId);
    const url = `${environment.eticApiUrl}UpdateCart`;
    const apiService = new APIService<any[]>(axios, new CacheService(), url, token);
   
    apiService.add(parentDocIds, url).then((res:any[]) => {
      setGlobalCart(res);
      handleCloseClick();
    }).catch((error: any) => {
      console.error("Failed to update cart:", error);
      const msg = error?.response?.data || error.message || "Unknown error";
      alert(`Error saving cart: ${msg}`);
    });
  };

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'remove',
      headerName: 'Remove',
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          variant="outlined"
          onClick={() => removeLocal(row.ticParentDocId)}
          startIcon={<FontAwesomeIcon icon={["fas","folder-minus"]} />}
        >
          Remove
        </Button>
      ),
    },
    { field: 'ticNumber',      headerName: 'TIC #',         width: 100 },
    { field: 'parentDocTitle', headerName: 'Title',         width: 240 },
    { field: 'docType',        headerName: 'Doc Type',     width: 100 },
    { field: 'infoType',       headerName: 'Info Type',    width: 100 },
    { field: 'scanRequested',  headerName: 'Scan Requested', width: 120 },
    { field: 'childDocCount',  headerName: 'Documents',     width: 110 },
  ], [removeLocal]);

  return (
    <Dialog open PaperProps={{ style: { minWidth: '1000px' } }}>
      <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Button
            id="removeAll"
            variant="outlined"
            color="primary"
            startIcon={<FontAwesomeIcon icon={["fas", "folder-minus"]} />}
            onClick={handleRemoveAllLocal}
          >
            Remove All ({localCart.length})
          </Button>
          <h2>Request Files</h2>
          <div style={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={localCart.map(item => ({
                id: item.ticNumber,
                ticParentDocId: item.parentDocId,
                ticNumber: item.ticNumber,
                parentDocTitle: item.parentDocTitle,
                infoType: item.infoType,
                docType: item.docType,
                scanRequested: item.scanRequested ? 'Yes' : '',
                childDocCount: item.childDocCount,
              }))}
              loading={loading}
              columns={columns}
              pageSizeOptions={[5, 10, 25, 50]}
              initialState={{ pagination: { paginationModel: { page: 0, pageSize: 5 } } }}
            />
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<FontAwesomeIcon icon={["fas", "circle-x"]} />}
          onClick={handleCloseClick}
        >
          {closeButtonText}
        </Button>
        <Button
          variant="outlined"
          startIcon={<FontAwesomeIcon icon={["fas", "save"]} />}
          onClick={onSave}
        >
          Save
        </Button>
        <Button
          variant="contained"
          startIcon={<FontAwesomeIcon icon={["fas", "file-download"]} />}
          onClick={() => handleDownloadItemsClick(localCart)}
        >
          {handleActionButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
